import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ClipboardList, 
  Users, 
  Calendar, 
  LineChart,
  School,
  Clock,
  AlertCircle
} from 'lucide-react';

// Import des composants (conservés les mêmes)
import GeneralInfoTab from './components/GeneralInfoTab';
import PlanningTab from './components/PlanningTab';
import StudentInfoTab from './components/StudentInfoTab';
import SummaryTab from './components/SummaryTab';

const API_CONFIG = {
  URL: 'https://table.naturavelo.net/api/v2/tables/moel0ns4k7yir44/records',
  TOKEN: 'JqpXXmIrMbKU0AUMjaMNH3zv40__Cbtf0sVkYo3a'
};

const TABS = [
  { 
    id: 'general', 
    label: 'Informations générales',
    icon: ClipboardList,
    Component: GeneralInfoTab 
  },
  { 
    id: 'students', 
    label: 'Élèves',
    icon: Users,
    Component: StudentInfoTab 
  },
  { 
    id: 'planning', 
    label: 'Planning',
    icon: Calendar,
    Component: PlanningTab 
  },
  { 
    id: 'summary', 
    label: 'Récapitulatif',
    icon: LineChart,
    Component: SummaryTab 
  }
];

// Animations variants
const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" }
  },
  exit: { 
    opacity: 0,
    y: -20,
    transition: { duration: 0.3 }
  }
};

const tabVariants = {
  inactive: { scale: 1 },
  active: { 
    scale: 1.02,
    transition: { type: "spring", stiffness: 300, damping: 20 }
  }
};

function App() {
  const [filters, setFilters] = useState({
    period: '',
    school: ''
  });
  const [periods, setPeriods] = useState([]);
  const [schools, setSchools] = useState([]);
  const [currentData, setCurrentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('general');
  const [requestedTab, setRequestedTab] = useState(null);

  // Logique de données (identique à l'original)
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_CONFIG.URL}?limit=1000`, {
        headers: { 'xc-token': API_CONFIG.TOKEN }
      });
      if (!response.ok) throw new Error('Erreur lors du chargement des données');
      const data = await response.json();
      const uniquePeriods = [...new Set(data.list.map(item => item.Période))]
        .filter(Boolean)
        .sort((a, b) => b.localeCompare(a));
      setPeriods(uniquePeriods);

      if (filters.period) {
        const schoolsForPeriod = [...new Set(
          data.list
            .filter(item => item.Période === filters.period)
            .map(item => item.Ecole)
        )].filter(Boolean).sort();
        setSchools(schoolsForPeriod);

        if (filters.school) {
          const schoolData = data.list.find(
            item => item.Période === filters.period && item.Ecole === filters.school
          );
          setCurrentData(schoolData || null);
        }
      }
    } catch (err) {
      setError(err.message);
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  }, [filters.period, filters.school]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdate = useCallback(async (updateData) => {
    if (!currentData?.Id) {
      throw new Error('Aucune école sélectionnée');
    }
    try {
      const response = await fetch(API_CONFIG.URL, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'xc-token': API_CONFIG.TOKEN
        },
        body: JSON.stringify({
          ...updateData,
          Id: currentData.Id
        })
      });
      if (!response.ok) throw new Error('Erreur lors de la mise à jour');
      setCurrentData(prev => ({ ...prev, ...updateData }));
      await fetchData();
    } catch (err) {
      console.error('Erreur de mise à jour:', err);
      throw err;
    }
  }, [currentData, fetchData]);

  const handleFilterChange = (type, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [type]: value };
      if (type === 'period') newFilters.school = '';
      return newFilters;
    });
    setCurrentData(null);
  };

  const handleTabRequest = useCallback((newTab) => {
    setRequestedTab(newTab);
  }, []);
  
  const handleTabChange = useCallback((newTab) => {
    setActiveTab(newTab);
    setRequestedTab(null);
  }, []);

  const CurrentTabComponent = TABS.find(tab => tab.id === activeTab)?.Component;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 p-4 md:p-8">
      <motion.div 
        initial="hidden"
        animate="visible"
        exit="exit"
        variants={containerVariants}
        className="max-w-7xl mx-auto"
      >
        {/* Card Container avec ombre dynamique */}
        <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-lg hover:shadow-xl transition-shadow duration-500">
          {/* En-tête avec gradient animé */}
          <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 rounded-t-3xl p-8 relative overflow-hidden">
            <motion.div 
              className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-violet-400/20"
              animate={{
                x: [0, 100, 0],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                ease: "linear"
              }}
            />
            <h1 className="text-4xl font-bold text-white text-center relative z-10">
              Informations réunion de cadrage SRAV 2024-2025 - Naturavélo
            </h1>
          </div>

          <div className="p-8">
            {/* Filtres avec animation */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8"
            >
              {/* Sélecteur de période */}
              <div className="relative group">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Clock className="w-4 h-4 mr-2 text-blue-500" />
                  Période
                </label>
                <select
                  value={filters.period}
                  onChange={(e) => handleFilterChange('period', e.target.value)}
                  className="w-full p-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    shadow-sm transition-all duration-300 hover:border-blue-400 outline-none"
                >
                  <option value="">Sélectionner une période</option>
                  {periods.map((period) => (
                    <option key={period} value={period}>{period}</option>
                  ))}
                </select>
                <motion.div 
                  className="absolute inset-0 border-2 border-blue-500 rounded-xl pointer-events-none opacity-0"
                  animate={{ opacity: filters.period ? 0.1 : 0 }}
                  transition={{ duration: 0.2 }}
                />
              </div>

              {/* Sélecteur d'école */}
              <div className="relative group">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <School className="w-4 h-4 mr-2 text-blue-500" />
                  École
                </label>
                <select
                  value={filters.school}
                  onChange={(e) => handleFilterChange('school', e.target.value)}
                  disabled={!filters.period || loading}
                  className="w-full p-3 bg-white border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                    shadow-sm transition-all duration-300 hover:border-blue-400 outline-none
                    disabled:bg-gray-50 disabled:border-gray-200 disabled:cursor-not-allowed"
                >
                  <option value="">Sélectionner une école</option>
                  {schools.map((school) => (
                    <option key={school} value={school}>{school}</option>
                  ))}
                </select>
              </div>
            </motion.div>

            {/* Message d'erreur animé */}
            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-xl"
                >
                  <div className="flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-400" />
                    <p className="ml-3 text-sm text-red-700">{error}</p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Loader animé */}
            <AnimatePresence>
              {loading && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <motion.div 
                    className="w-16 h-16 border-4 border-blue-400 border-t-transparent rounded-full"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  />
                  <motion.p 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="mt-4 text-gray-600"
                  >
                    Chargement en cours...
                  </motion.p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Contenu principal avec animation */}
            <AnimatePresence mode="wait">
              {currentData && !loading && (
                <motion.div
                  key="content"
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  variants={containerVariants}
                >
                  {/* Navigation des onglets modernisée */}
                  <nav className="flex space-x-2 bg-gray-100/50 backdrop-blur-sm p-2 rounded-2xl mb-6">
                    {TABS.map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <motion.button
                          key={tab.id}
                          onClick={() => handleTabRequest(tab.id)}
                          variants={tabVariants}
                          initial="inactive"
                          animate={activeTab === tab.id ? "active" : "inactive"}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`
                            flex items-center justify-center space-x-2
                            flex-1 px-4 py-3 rounded-xl
                            text-sm font-medium
                            transition-colors duration-300
                            ${activeTab === tab.id 
                              ? 'bg-white text-blue-600 shadow-lg' 
                              : 'text-gray-600 hover:bg-white/50 hover:text-blue-500'}
                          `}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="hidden sm:inline">{tab.label}</span>
                        </motion.button>
                      );
                    })}
                  </nav>

                  {/* Contenu des onglets avec animation */}
                  <motion.div 
                    className="bg-white rounded-2xl p-6 shadow-lg"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                  >
                    <CurrentTabComponent
                      data={currentData}
                      onUpdate={handleUpdate}
                      onTabChange={handleTabChange}
                      currentTab={activeTab}
                      requestedTab={requestedTab}
                    />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

export default App;